import { useLocation, useNavigate } from "react-router";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import MainTable from "../../components/tables/MainTable";
import endpoints from "../../services/endpoints";
import { TableDetailsItem } from "../../components/tables/tableFunctions";
import useApi from "../../services/useApi";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { showToast } from "../../components/toastMessage/toastSlice";
import useTranslation from "../../components/CustomHooks/useTranslation";
import { adminContents } from "../AdminManagement/components/AdminManagementContent";
import CustomModal from "../../components/CustomModal";
import { useState } from "react";
import { setUpfrontPercentage } from "../../store/slices/upfrontPercentageSlice";
import usePrivilege from "../../components/CustomHooks/usePrivilege";
import { useSelector } from "react-redux";
import { setPreviousUrl } from "../../store/slices/geolocationSlice";
import { buildMnoGrandTotalRow, hasNoMnosAdded } from "./mnoListHelpers";

export default function ListofMNOs() {
  const navigation = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { mnoList } = endpoints;
  const location = useLocation();
  const { PutAPI } = useApi();
  const { translate } = useTranslation(adminContents);
  const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
  const { recordCount, loading: listLoading } = useSelector((state: RootState) => state.list);
  // Upfront Service Fee is greyed out until at least one MNO exists (empty state design)
  const noMnosAdded = hasNoMnosAdded(recordCount, listLoading, new URLSearchParams(location.search));
  const mnoPrivileges = {
    list: usePrivilege("MM1"),
    edit: usePrivilege("MM2"),
    serviceFee: usePrivilege("MM3"),
    add: usePrivilege("MM4"),
    addPurchaseAmount: usePrivilege("MM5"),
    view: usePrivilege("MM6")
  }
  const TableDetails: { [key: string]: TableDetailsItem } = {
    "MNO Name": {
      id: "mno_name",
      custom: ["name"],
    },
    "MNO Contract Start Date": {
      id: "contract_start_date",
      // className: "min-w-[197px]",
    },
    "MNO Contract End Date": {
      id: "contract_end_date",
      // className: "min-w-[200px]",
    },
    "eTop Up Balance": {
      id: "etopup_balance",
      // className: "min-w-[196px]",
    },
    "Bundle Balance": {
      id: "bundle_balance",
    },
    Actions: {
      id: "actions",
      className: "min-w-[200px]", // Adding className here
      actionTypes: ["view", "edit"],
    },
  };
  const handleActions = async (id: string, key: string, text?: string) => {
    if (key === "view") {
      dispatch(setPreviousUrl(location.pathname + location.search))
      navigation(`/mno-management/list-of-mnos/view-mno/${id}`);
    } else if (key === "edit") {
      dispatch(setPreviousUrl(location.pathname + location.search))
      navigation(`/mno-management/list-of-mnos/edit-mno/${id}`);
    }
  }
  const addButtonClick = () => {
    navigation("/mno-management/list-of-mnos/add-mno");
  };
  const staticText = {
    emptyScreenText: {
      heading1: translate("There are no MNO's added yet"),
      heading2: translate("Click “Add MNO” to create one"),
    },
  };
  const handleUpfront = async (value: string) => {
    setLoading(true);
    dispatch(setUpfrontPercentage(value))
    let payload = {
      "max_upfront_service_fee_percent": Number(value),
    };
    let response = await PutAPI("mnos/upfront/", payload);
    if (!(response)?.error) {
      dispatch(
        showToast({
          type: "success",
          message: 'Service fee added successfully',
        })
      );
      setIsModalOpen(false);
      setLoading(false);
    } else {
      console.log(response, 'API ERROR')
      dispatch(
        showToast({
          type: "error",
          message: response?.data?.response?.data?.message,
        })
      );
      setIsModalOpen(false);
      setLoading(false);
    }
  }
  return (
    <div>
      <Breadcrumbs
        navPath={["MNO Management", "List of MNO’s"]}
        heading="List of MNO’s"
        noNavigation
        links={pathname ? [pathname] : ["/mno-management/list-of-mnos"]}
        button={{
          buttonName: "Add MNO",
          className: "w-[120px]",
          buttonOnclick: addButtonClick, // Corrected function type
        }}
        secondaryButton={{
          buttonName: "Upfront Service Fee Details",
          className: "px-3 h-[45px]",
          buttonOnclick: () => setIsModalOpen(true), // Corrected function type
          disabled: noMnosAdded,
        }}
        hasAddPrivilege={mnoPrivileges.add}
        upfront={true}
        list={true}
      />
      <MainTable
        staticText={staticText}
        mainEndPoint={mnoList}
        TableDetails={TableDetails}
        handleActions={handleActions}
        params={{ page_number: "1" }}
        searchPlaceholder={"Search by mno name"}
        filters={true}
        exporting={true}
        notfoundRoute={{ state: { route: '/mno-management/list-of-mnos' } }}
        hasListPrivilege={mnoPrivileges.list}
        hasViewMDPrivilege={mnoPrivileges.view}
        hasEditMDPrivilege={mnoPrivileges.edit}
        mnoList={true}
        tableVariant="yellow"
        getGrandTotalRow={(response) => buildMnoGrandTotalRow(response as Parameters<typeof buildMnoGrandTotalRow>[0])}
      />
      {isModalOpen && (
        <CustomModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Set Service Fee"
          subText="The maximum upfront service fee can be provided to users or merchants, including Master Distributor (MDST), Distributor (DST), Sub Distributor (SDST), Agent (AGT), and Retailer (RET)."
          primaryButtonText="Update"
          secondaryButtonText={translate("Cancel")}
          handelSubmit={(text) => handleUpfront(text!)}
          buttonColor={"bg-p_primary"}
          textArea={false}
          buttonLoading={loading}
          buttonWidth="w-[108px]"
          input={{
            labelName: "Maximum upfront service fee percentage",
            id: "upfront_service_fee_percentage",
          }}
        />
      )}
    </div>
  );
}
